import { TabModel } from '@anecdotes/design-system/dropdown-menu';
import { LoaderManagerService } from '@anecdotes/design-system/loaders';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  SkipSelf,
  DestroyRef,
} from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, filter, firstValueFrom, map, take } from 'rxjs';
import { AccessTabs, CompanyResponse, TrustCenterFacadeService, TrustCenterSharedModalsService, UserResponse } from '@anecdotes/shared-trust-center';
import { TrustCenterModalService } from '../../../../services/trustcenter-modals/trustcenter-modals.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouteParams } from '@anecdotes/configuration-data-access';
import { ActivatedRoute, Router } from '@angular/router';
import { SearchDefinitionModel } from '@anecdotes/design-system/data-manipulation';

@Component({
  selector: 'app-access',
  templateUrl: './access.component.html',
  styleUrls: ['./access.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AccessComponent implements OnInit, OnDestroy {
  readonly Tabs = AccessTabs;
  readonly userSearchDefinitions: SearchDefinitionModel<UserResponse>[] = [
    {
      propertyId: 'name',
      propertySelector: (user: UserResponse) => `${user.first_name} ${user.last_name}`,
    },
    {
      propertyId: 'company',
      propertySelector: (user: UserResponse) => user.company,
    },
    {
      propertyId: 'job',
      propertySelector: (user: UserResponse) => user.job_title,
    },
    {
      propertyId: 'email',
      propertySelector: (user: UserResponse) => user.email,
    },
  ];
  readonly companySearchDefinitions: SearchDefinitionModel<CompanyResponse>[] = [
    {
      propertyId: 'name',
      propertySelector: (company: CompanyResponse) => company.name,
    },
  ];

  tabs: TabModel[];

  currentTabId: AccessTabs;
  isAllLoaded$: Observable<boolean>;
  openRequests: UserResponse[];
  otherRequests: UserResponse[];
  companies: CompanyResponse[];
  filteredOpenRequests$ = new BehaviorSubject<UserResponse[]>([]);
  filteredOtherRequests$ = new BehaviorSubject<UserResponse[]>([]);
  filteredCompanies$ = new BehaviorSubject<CompanyResponse[]>([]);

  constructor(
    private cd: ChangeDetectorRef,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    @SkipSelf() private loaderManager: LoaderManagerService,
    private trustCenterFacadeService: TrustCenterFacadeService,
    private trustCenterModalService: TrustCenterModalService,
    private readonly trustCenterSharedModalsService: TrustCenterSharedModalsService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.initializeValues();
    this.initializeListeners();
  }

  ngOnDestroy(): void {
    this.loaderManager.hide();
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.${key}`;
  }

  async changeTab(tabId: AccessTabs, search?: string, companyId?: string): Promise<void> {
    this.currentTabId = tabId;
    await this.setQueryParam(tabId, search, companyId);
    this.cd.detectChanges();
  }

  goToAccessManagerTab(): void {
    this.changeTab(AccessTabs.Users);
  }

  inviteUser(): void {
    this.trustCenterModalService.openInviteUserModal();
  }

  addCompany(): void {
    this.trustCenterSharedModalsService.openAddEditCompanySidePanel();
  }

  handleRequestsSearch(data: UserResponse[]): void {
    this.filteredOpenRequests$.next(data);
  }

  handleCompaniesSearch(data: CompanyResponse[]): void {
    this.filteredCompanies$.next(data);
  }

  handleOtherRequestsSearch(data: UserResponse[]): void {
    this.filteredOtherRequests$.next(data);
  }

  private initializeValues(): void {
    const queryParamTab = this.activatedRoute.snapshot.queryParamMap.get(RouteParams.trustCenter.tab) as AccessTabs;
    const currentTab = Object.values(AccessTabs).includes(queryParamTab) ? queryParamTab : AccessTabs.Requests;
    this.changeTab(currentTab);

    this.isAllLoaded$ = combineLatest([
      this.trustCenterFacadeService.areUsersLoaded(),
      this.trustCenterFacadeService.areCompaniesLoaded(),
    ]).pipe(map(([usersLoaded, companiesLoaded]) => usersLoaded && companiesLoaded));
  }

  private async openCompanySidePanel(companyId: number): Promise<void> {
    const comapny = await firstValueFrom(this.trustCenterFacadeService.getCompanyById(companyId));
    if (comapny) {
      this.trustCenterModalService.openCompanySidePanel(comapny);
    }
  }

  private initializeListeners(): void {
    const companyId = this.activatedRoute.snapshot.queryParamMap.get(RouteParams.trustCenter.companyId);
    this.isAllLoaded$.pipe(filter(Boolean), take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (companyId) {
        this.openCompanySidePanel(parseInt(companyId));
      }
    });

    this.router.routerState.root.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const queryParamTab = params[RouteParams.trustCenter.tab] as AccessTabs;
      const currentTab = Object.values(AccessTabs).includes(queryParamTab) ? queryParamTab : AccessTabs.Requests;
      const search = params[RouteParams.trustCenter.search];
      const companyId = params[RouteParams.trustCenter.companyId];

      this.changeTab(currentTab, search, companyId);
    });

    combineLatest([
      this.trustCenterFacadeService.getOpenUserRequests(),
      this.trustCenterFacadeService.getNonNewUserRequests(),
      this.trustCenterFacadeService.getCompanies(),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([openRequests, otherRequests, companies]) => {
        this.tabs = [
          {
            tabId: AccessTabs.Requests,
            icon: openRequests.length > 0 ? 'in_review_dot' : 'in_review',
            translationKey: this.buildTranslationKey('openRequestsCount'),
            count: openRequests.length || undefined,
          },
          {
            tabId: AccessTabs.Companies,
            icon: 'company',
            translationKey: this.buildTranslationKey('companyCount'),
            count: companies.length || undefined,
          },
          {
            tabId: AccessTabs.Users,
            icon: 'user-management',
            translationKey: this.buildTranslationKey('userCount'),
            count: otherRequests.length || undefined,
          },
        ];

        this.openRequests = openRequests;
        this.otherRequests = otherRequests;
        this.companies = companies;
        this.cd.detectChanges();
      });
  }

  private async setQueryParam(tabId: AccessTabs, search?: string, companyId?: string): Promise<void> {
    const queryParams = {
      [RouteParams.trustCenter.tab]: tabId,
      [RouteParams.trustCenter.search]: search || null,
      [RouteParams.trustCenter.companyId]: companyId || null,
    };
    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
